/**
 * The settings area's own navigation, shown beside the app sidebar.
 *
 * The full tree is declared up front, matching the panels the onboarding wizard
 * collects, so the shape of the area is visible from day one. Panels without a
 * `to` render disabled — they are the ones still to be built, and showing them
 * greyed is more honest than pretending the area is only one page wide.
 *
 * Adding a panel is a `to:` value here plus a route in App.tsx.
 *
 * Zoho-only panels (`backend: "zoho"`) talk to Zoho Books APIs or ZOUR-19 GST
 * surfaces; Tally tenants use the shared masters / GL / cash-bank flows instead.
 */

import type { AccountingBackendGate } from "../../../../config/accounting";
import { matchesAccountingBackendGate } from "../../../../config/accounting";

export interface SettingsNavItem {
  /** Stable key, also the React key. */
  id: string;
  label: string;
  /** Router path. Omitted while the panel does not exist yet. */
  to?: string;
  /**
   * When set, only that accounting backend sees the item. Omit for panels that
   * work on both (company details, chart of accounts, LUT, team board).
   */
  backend?: AccountingBackendGate;
}

export interface SettingsNavSection {
  id: string;
  /** Group heading. Omitted for the first group, which sits under the title. */
  label?: string;
  items: SettingsNavItem[];
}

/** Base path for the whole settings area. */
export const SETTINGS_BASE_PATH = "/settings";

/** Where `/settings` lands. */
export const SETTINGS_DEFAULT_PATH = `${SETTINGS_BASE_PATH}/company-details`;

/**
 * Where the "Back to workbench" link goes.
 *
 * Settings is a full-screen mode with the app sidebar hidden, so it needs one
 * unambiguous way out. A fixed destination beats history-back, which lands
 * somewhere different depending on how the user got here.
 */
export const WORKBENCH_PATH = "/v1";

export const SETTINGS_NAV_SECTIONS: SettingsNavSection[] = [
  {
    id: "basics",
    items: [{ id: "company-details", label: "Company details", to: SETTINGS_DEFAULT_PATH }],
  },
  {
    id: "masters",
    label: "Masters",
    items: [
      {
        id: "chart-of-accounts",
        label: "Chart of Accounts",
        to: `${SETTINGS_BASE_PATH}/chart-of-accounts`,
      },
      {
        id: "gst",
        label: "GST",
        to: `${SETTINGS_BASE_PATH}/gst`,
        // ZOUR-19 settings panel is Zoho Books–backed for now.
        backend: "zoho",
      },
      // ZOUR-65: the company's GST registrations. Sits under GST because that
      // is the question it answers a second time — "which GSTIN" — and above
      // LUT because LUT is per registration (phase 4). Zoho Locations API.
      {
        id: "locations",
        label: "Locations",
        to: `${SETTINGS_BASE_PATH}/locations`,
        backend: "zoho",
      },
      { id: "lut", label: "LUT", to: `${SETTINGS_BASE_PATH}/lut` },
      {
        id: "tds-tcs",
        label: "TDS & TCS",
        to: `${SETTINGS_BASE_PATH}/tds-tcs`,
        backend: "zoho",
      },
      {
        id: "banks",
        label: "Banks",
        to: `${SETTINGS_BASE_PATH}/banks`,
        // Creates accounts in Zoho Books; Tally banks sync via admin / cash-bank.
        backend: "zoho",
      },
      {
        id: "customers-vendors",
        label: "Customers & vendors",
        to: `${SETTINGS_BASE_PATH}/customers-vendors`,
      },
      {
        id: "currency",
        label: "Currency",
        to: `${SETTINGS_BASE_PATH}/currency`,
        backend: "zoho",
      },
    ],
  },
  {
    id: "team",
    label: "Team & rules",
    items: [
      {
        id: "team-assignment",
        label: "Team assignment",
        to: `${SETTINGS_BASE_PATH}/team-assignment`,
      },
      // approval-rules retired 2026-08-21 — the team board's per-function
      // policies replaced it; page kept in the tree, unrouted.
      {
        id: "escalations-access",
        label: "Escalations & access",
        to: `${SETTINGS_BASE_PATH}/escalations-access`,
      },
    ],
  },
];

/** True when `pathname` should light up `item`. */
export const isSettingsNavItemActive = (item: SettingsNavItem, pathname: string): boolean =>
  item.to !== undefined && pathname.startsWith(item.to);

/** Hides Zoho-only / Tally-only settings panels for the wrong tenant. */
export const isSettingsNavItemVisible = (item: SettingsNavItem): boolean =>
  matchesAccountingBackendGate(item.backend);
